/**
 * NeoCoreReportesPagoPage — bandeja del operador para conciliar reportes
 * de pago de cuota de crédito enviados por el asociado desde la app.
 *
 * Flujo:
 *   1. El asociado en NeoApp sube comprobante + monto + cuenta destino.
 *   2. El reporte queda en estado 'pendiente'.
 *   3. El operador:
 *        - abre el comprobante (signed URL del bucket 'reportes')
 *        - verifica que el monto y la referencia coinciden con su extracto
 *        - aplica el pago via conciliar_reporte_pago_credito_admin
 *        - o rechaza con motivo via rechazar_reporte_pago_admin
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CheckCircle,
  Coins,
  Hourglass,
  ImageSquare,
  MagnifyingGlass,
  Receipt,
  Warning,
  XCircle,
} from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'

type EstadoReporte = 'pendiente' | 'conciliado' | 'rechazado'
type ConceptoReporte = 'CREDITO_CUOTA' | 'AHORRO_DEPOSITO' | 'PLAZO_FIJO_APERTURA' | 'OTRO'

type ReporteRow = {
  id: string
  asociado_id: string
  asociado_no: string
  asociado_nombre: string
  concepto: ConceptoReporte
  monto: number
  fecha_movimiento: string
  forma_pago: string
  numero_referencia: string | null
  referencia_banco: string | null
  notas_asociado: string | null
  estado: EstadoReporte
  comprobante_storage_path: string | null
  created_at: string
  credito_id: string | null
  credito_cuota_id: string | null
  cuota_numero: number | null
  credito_tipo_nombre: string | null
  cuenta_banco: string | null
  cuenta_alias: string | null
  cuenta_numero: string | null
  cuenta_contable_codigo: string | null
  rechazo_motivo: string | null
  conciliado_at: string | null
  // Lectura de la boleta con IA (0413)
  ocr_extraido: OcrBoleta | null
  ocr_monto: number | null
  monto_conciliado: number | null
}

type OcrBoleta = {
  ok?: boolean
  extraido?: {
    banco_destino: string | null; cuenta_destino: string | null; monto: number | null; fecha: string | null
    referencia: string | null; es_comprobante_valido: boolean; motivo_invalido: string | null; confianza: 'alta' | 'media' | 'baja'
  }
  verificacion?: { coincide_cuenta_institucional?: boolean; coincide_monto?: boolean; revisar?: boolean; motivos?: string[] }
}

// Base64 de un blob (para mandar la imagen del comprobante al OCR).
const blobToBase64 = (b: Blob) => new Promise<string>((res, rej) => {
  const fr = new FileReader()
  fr.onload = () => res(String(fr.result).split(',')[1] ?? '')
  fr.onerror = rej
  fr.readAsDataURL(b)
})

const CONCEPTO_LABEL: Record<string, { label: string; color: string; bg: string }> = {
  CREDITO_CUOTA:       { label: 'Cuota crédito', color: '#7a4f00', bg: 'rgba(245,158,11,0.12)' },
  AHORRO_DEPOSITO:     { label: 'Depósito ahorro', color: '#0f8a57', bg: 'rgba(15,138,87,0.12)' },
  PLAZO_FIJO_APERTURA: { label: 'Apertura PF',   color: '#5b21b6', bg: 'rgba(91,33,182,0.10)' },
  OTRO:                { label: 'Otro',          color: '#6b7280', bg: 'rgba(107,114,128,0.10)' },
}

const fmtQ = (n: number) =>
  'Q ' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const fmtFecha = (iso: string) => {
  if (!iso) return '—'
  const d = new Date(iso.slice(0, 10) + 'T12:00:00')
  return d.toLocaleDateString('es-GT', { day: '2-digit', month: 'short', year: 'numeric' })
}

const fmtFechaHora = (iso: string) => {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString('es-GT', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: 'America/Guatemala',
  })
}

const ESTADO_BADGE: Record<EstadoReporte, { label: string; color: string; bg: string }> = {
  pendiente:  { label: 'Pendiente',  color: 'var(--warning, #d97706)', bg: 'rgba(217,119,6,0.10)' },
  conciliado: { label: 'Aplicado',   color: 'var(--ok-ink, #0f8a57)',  bg: 'rgba(15,138,87,0.10)' },
  rechazado:  { label: 'Rechazado',  color: 'var(--danger, #a32d2d)',  bg: 'rgba(163,45,45,0.10)' },
}

export function NeoCoreReportesPagoPage() {
  const [rows, setRows] = useState<ReporteRow[]>([])
  const [loading, setLoading] = useState(true)
  const [filtro, setFiltro] = useState<'pendiente' | 'todos'>('pendiente')
  const [conceptoFiltro, setConceptoFiltro] = useState<'todos' | 'CREDITO_CUOTA' | 'AHORRO_DEPOSITO' | 'PLAZO_FIJO_APERTURA'>('todos')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<ReporteRow | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    // Cargar TODOS los conceptos (crédito, ahorro, PF). El filtro por concepto
    // se hace después en cliente para que Nataly pueda ver la mezcla completa
    // y elegir qué revisar primero.
    const { data, error } = await supabase
      .from('reporte_pago_asociado')
      .select(`
        id, asociado_id, concepto, monto, fecha_movimiento, forma_pago,
        numero_referencia, referencia_banco, notas_asociado, estado, comprobante_storage_path,
        created_at, credito_id, credito_cuota_id, rechazo_motivo, conciliado_at,
        ocr_extraido, ocr_monto, monto_conciliado,
        asociado:asociado_id ( no_asociado, nombre_completo ),
        cuota:credito_cuota_id ( numero ),
        credito:credito_id ( tipo:tipo_credito_id ( nombre ) ),
        cuenta:cuenta_bancaria_propia_id ( banco, alias, numero_cuenta, cuenta_contable_codigo )
      `)
      .order('created_at', { ascending: false })
      .limit(200)
    setLoading(false)
    if (error) {
      console.error('reporte_pago_asociado fetch error:', error)
      setRows([])
      return
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mapped: ReporteRow[] = (data ?? []).map((r: any) => ({
      id: r.id,
      asociado_id: r.asociado_id,
      asociado_no: r.asociado?.no_asociado ?? '—',
      asociado_nombre: r.asociado?.nombre_completo ?? '—',
      concepto: (r.concepto ?? 'OTRO') as ConceptoReporte,
      monto: Number(r.monto),
      fecha_movimiento: r.fecha_movimiento,
      forma_pago: r.forma_pago,
      numero_referencia: r.numero_referencia,
      referencia_banco: r.referencia_banco ?? null,
      notas_asociado: r.notas_asociado,
      estado: r.estado as EstadoReporte,
      comprobante_storage_path: r.comprobante_storage_path,
      created_at: r.created_at,
      credito_id: r.credito_id,
      credito_cuota_id: r.credito_cuota_id,
      cuota_numero: r.cuota?.numero ?? null,
      credito_tipo_nombre: r.credito?.tipo?.nombre ?? null,
      cuenta_banco: r.cuenta?.banco ?? null,
      cuenta_alias: r.cuenta?.alias ?? null,
      cuenta_numero: r.cuenta?.numero_cuenta ?? null,
      cuenta_contable_codigo: r.cuenta?.cuenta_contable_codigo ?? null,
      rechazo_motivo: r.rechazo_motivo,
      conciliado_at: r.conciliado_at,
      ocr_extraido: r.ocr_extraido ?? null,
      ocr_monto: r.ocr_monto == null ? null : Number(r.ocr_monto),
      monto_conciliado: r.monto_conciliado == null ? null : Number(r.monto_conciliado),
    }))
    setRows(mapped)
  }, [])

  useEffect(() => { void load() }, [load])

  const filtered = useMemo(() => {
    let out = rows
    if (filtro === 'pendiente') out = out.filter((r) => r.estado === 'pendiente')
    if (conceptoFiltro !== 'todos') out = out.filter((r) => r.concepto === conceptoFiltro)
    if (search.trim()) {
      const s = search.trim().toLowerCase()
      out = out.filter((r) =>
        r.asociado_no.toLowerCase().includes(s) ||
        r.asociado_nombre.toLowerCase().includes(s) ||
        (r.numero_referencia ?? '').toLowerCase().includes(s) ||
        r.monto.toFixed(2).includes(s)
      )
    }
    return out
  }, [rows, filtro, conceptoFiltro, search])

  const stats = useMemo(() => {
    const p = rows.filter((r) => r.estado === 'pendiente')
    return {
      pendientes: p.length,
      pendientesMonto: p.reduce((a, r) => a + r.monto, 0),
      conciliados: rows.filter((r) => r.estado === 'conciliado').length,
      rechazados: rows.filter((r) => r.estado === 'rechazado').length,
    }
  }, [rows])

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 22, maxWidth: 1280, margin: '0 auto' }}>

      {/* Header */}
      <div>
        <p style={{
          margin: 0, fontSize: 10, fontWeight: 800, letterSpacing: 2,
          color: 'var(--brand-700)', textTransform: 'uppercase',
        }}>Reportes de pago</p>
        <h1 style={{ margin: '6px 0 0', fontSize: 28, fontWeight: 800, letterSpacing: -0.5 }}>
          Bandeja de conciliación
        </h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--ink-soft)' }}>
          Reportes que el asociado envió desde la app con comprobante bancario. Cuando coincidan con tu extracto, aplicalos al crédito.
        </p>
      </div>

      {/* Stats hero */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12,
      }}>
        <StatCard
          eyebrow="Pendientes"
          icon={<Hourglass size={18} weight="duotone" />}
          value={String(stats.pendientes)}
          sub={fmtQ(stats.pendientesMonto) + ' por aplicar'}
          accent="warn"
        />
        <StatCard
          eyebrow="Aplicados"
          icon={<CheckCircle size={18} weight="duotone" />}
          value={String(stats.conciliados)}
          sub="cuotas acreditadas"
          accent="ok"
        />
        <StatCard
          eyebrow="Rechazados"
          icon={<XCircle size={18} weight="duotone" />}
          value={String(stats.rechazados)}
          sub="con motivo registrado"
          accent="muted"
        />
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <FiltroChip active={filtro === 'pendiente'} onClick={() => setFiltro('pendiente')}>
            Pendientes
          </FiltroChip>
          <FiltroChip active={filtro === 'todos'} onClick={() => setFiltro('todos')}>
            Todos
          </FiltroChip>
        </div>
        <div style={{ width: 1, height: 22, background: 'var(--hairline)' }} />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <FiltroChip active={conceptoFiltro === 'todos'} onClick={() => setConceptoFiltro('todos')}>
            Todos los conceptos
          </FiltroChip>
          <FiltroChip active={conceptoFiltro === 'CREDITO_CUOTA'} onClick={() => setConceptoFiltro('CREDITO_CUOTA')}>
            Cuotas de crédito
          </FiltroChip>
          <FiltroChip active={conceptoFiltro === 'AHORRO_DEPOSITO'} onClick={() => setConceptoFiltro('AHORRO_DEPOSITO')}>
            Depósitos a ahorro
          </FiltroChip>
          <FiltroChip active={conceptoFiltro === 'PLAZO_FIJO_APERTURA'} onClick={() => setConceptoFiltro('PLAZO_FIJO_APERTURA')}>
            Aperturas PF
          </FiltroChip>
        </div>
        <div style={{
          flex: 1, minWidth: 240, display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 12px', background: 'var(--surface)', borderRadius: 10,
          border: '1px solid var(--hairline)',
        }}>
          <MagnifyingGlass size={14} weight="bold" color="var(--ink-soft)" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por asociado, NEO, referencia o monto…"
            style={{
              border: 0, outline: 0, background: 'transparent', flex: 1,
              fontSize: 13, color: 'var(--ink)',
            }}
          />
        </div>
      </div>

      {/* Lista */}
      {loading ? (
        <div style={{ padding: 60, textAlign: 'center', color: 'var(--ink-soft)' }}>Cargando…</div>
      ) : filtered.length === 0 ? (
        <EmptyState />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map((r) => (
            <ReporteCard key={r.id} r={r} onOpen={() => setSelected(r)} />
          ))}
        </div>
      )}

      {selected && (
        <ReporteDrawer
          reporte={selected}
          onClose={() => setSelected(null)}
          onResolved={() => { setSelected(null); void load() }}
        />
      )}
    </div>
  )
}

// ─── pieces ──────────────────────────────────────────────────────

function StatCard({
  eyebrow, icon, value, sub, accent,
}: {
  eyebrow: string
  icon: React.ReactNode
  value: string
  sub: string
  accent: 'warn' | 'ok' | 'muted'
}) {
  const colors = {
    warn: { ink: 'var(--warning, #d97706)' },
    ok: { ink: 'var(--ok-ink, #0f8a57)' },
    muted: { ink: 'var(--ink-soft)' },
  }[accent]
  return (
    <div style={{
      padding: 16, borderRadius: 16,
      background: 'linear-gradient(135deg, #fff 0%, var(--brand-50) 100%)',
      border: '1px solid var(--hairline)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: colors.ink }}>
        {icon}
        <p style={{
          margin: 0, fontSize: 10, fontWeight: 800, letterSpacing: 1.5,
          textTransform: 'uppercase',
        }}>{eyebrow}</p>
      </div>
      <p style={{
        margin: '6px 0 0', fontSize: 28, fontWeight: 800, color: 'var(--ink)',
        letterSpacing: '-0.02em', fontFeatureSettings: '"tnum"',
      }}>{value}</p>
      <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--ink-soft)' }}>{sub}</p>
    </div>
  )
}

function FiltroChip({
  active, onClick, children,
}: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        all: 'unset', cursor: 'pointer',
        padding: '7px 14px', borderRadius: 99,
        fontSize: 12, fontWeight: 700, letterSpacing: 0.3,
        background: active ? 'var(--brand-600)' : 'var(--surface)',
        color: active ? '#fff' : 'var(--ink)',
        border: '1px solid ' + (active ? 'var(--brand-600)' : 'var(--hairline)'),
      }}
    >
      {children}
    </button>
  )
}

function ReporteCard({ r, onOpen }: { r: ReporteRow; onOpen: () => void }) {
  const badge = ESTADO_BADGE[r.estado]
  const concepto = CONCEPTO_LABEL[r.concepto] ?? CONCEPTO_LABEL.OTRO
  const contextoLinea = r.concepto === 'CREDITO_CUOTA'
    ? `${r.credito_tipo_nombre ?? 'Crédito'}${r.cuota_numero != null ? ` · cuota ${r.cuota_numero}` : ''}`
    : r.concepto === 'AHORRO_DEPOSITO'
      ? 'Depósito a cuenta de ahorro corriente'
      : r.concepto === 'PLAZO_FIJO_APERTURA'
        ? 'Apertura de plazo fijo'
        : 'Reporte de pago'
  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        all: 'unset', cursor: 'pointer',
        display: 'grid',
        gridTemplateColumns: '1fr auto auto',
        gap: 16,
        alignItems: 'center',
        padding: '14px 18px',
        background: '#fff',
        border: '1px solid var(--hairline)',
        borderRadius: 14,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{
            fontSize: 9, fontWeight: 800, letterSpacing: 0.5,
            padding: '3px 7px', borderRadius: 4,
            background: 'var(--n-100)', color: 'var(--n-600)',
          }}>NEO-{r.asociado_no}</span>
          <span style={{
            fontSize: 9, fontWeight: 800, letterSpacing: 0.5,
            padding: '3px 7px', borderRadius: 4,
            background: concepto.bg, color: concepto.color,
            textTransform: 'uppercase',
          }}>{concepto.label}</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>
            {r.asociado_nombre}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 10, fontSize: 11, color: 'var(--ink-soft)', flexWrap: 'wrap' }}>
          <span>{contextoLinea}</span>
          <span>·</span>
          <span>{fmtFecha(r.fecha_movimiento)} {r.forma_pago === 'TRANSFERENCIA' ? '· transferencia' : '· depósito'}</span>
          {r.numero_referencia && (<><span>·</span><span>ref {r.numero_referencia}</span></>)}
        </div>
        <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>
          Enviado {fmtFechaHora(r.created_at)}
        </div>
      </div>

      <div style={{
        fontSize: 18, fontWeight: 800, color: 'var(--ink)',
        fontFeatureSettings: '"tnum"', textAlign: 'right',
      }}>
        {fmtQ(r.monto)}
      </div>

      <div style={{
        padding: '5px 10px', borderRadius: 99,
        background: badge.bg, color: badge.color,
        fontSize: 11, fontWeight: 800, letterSpacing: 0.5,
        textTransform: 'uppercase',
      }}>
        {badge.label}
      </div>
    </button>
  )
}

function EmptyState() {
  return (
    <div style={{
      padding: '60px 20px', textAlign: 'center',
      background: 'var(--surface)', borderRadius: 16, border: '1px dashed var(--hairline)',
    }}>
      <div style={{
        width: 60, height: 60, borderRadius: 99, background: 'var(--brand-50)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--brand-700)',
      }}>
        <Receipt size={26} weight="duotone" />
      </div>
      <p style={{ margin: '14px 0 4px', fontSize: 16, fontWeight: 700, color: 'var(--ink)' }}>
        Sin reportes que mostrar
      </p>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-soft)' }}>
        Cuando un asociado reporte un pago desde la app, va a aparecer acá para que lo verifiques y apliques.
      </p>
    </div>
  )
}

// ─── drawer ──────────────────────────────────────────────────────

function ReporteDrawer({
  reporte, onClose, onResolved,
}: {
  reporte: ReporteRow
  onClose: () => void
  onResolved: () => void
}) {
  const [comprobanteUrl, setComprobanteUrl] = useState<string | null>(null)
  const [comprobanteLoading, setComprobanteLoading] = useState(false)
  const [cajaActiva, setCajaActiva] = useState<{ id: string; nombre: string } | null>(null)
  const [cajaError, setCajaError] = useState<string | null>(null)
  const [working, setWorking] = useState<'aplicar' | 'rechazar' | null>(null)
  const [motivoRechazo, setMotivoRechazo] = useState('')
  const [mostrarRechazo, setMostrarRechazo] = useState(false)
  const [resultMsg, setResultMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)
  const [cuentasEntrada, setCuentasEntrada] = useState<Array<{ codigo: string; nombre: string; tipo: string }>>([])
  const [entradaCodigo, setEntradaCodigo] = useState('')
  const [refBoleta, setRefBoleta] = useState(reporte.numero_referencia ?? '')
  const [refBanco, setRefBanco] = useState(reporte.referencia_banco ?? '')
  // Pendiente de la cuota (para detectar abonos parciales en conciliación · ADR-017)
  const [cuotaPend, setCuotaPend] = useState<number | null>(null)
  // Lectura de la boleta con IA + monto real a aplicar (0413)
  const [ocr, setOcr] = useState<OcrBoleta | null>(reporte.ocr_extraido)
  const [leyendo, setLeyendo] = useState(false)
  const [ocrError, setOcrError] = useState<string | null>(null)
  const ocrMonto = ocr?.extraido?.es_comprobante_valido !== false && ocr?.extraido?.monto != null ? Number(ocr.extraido.monto) : null
  const [montoAplicar, setMontoAplicar] = useState<string>(
    (reporte.ocr_monto ?? reporte.monto).toFixed(2))
  const [notaMonto, setNotaMonto] = useState('')
  const [confirmoDif, setConfirmoDif] = useState(false)
  const montoNum = Number(montoAplicar) || 0
  const difOcr = ocrMonto != null && Math.abs(ocrMonto - montoNum) > 0.009
  const difReportado = Math.abs(reporte.monto - montoNum) > 0.009

  const leerBoleta = async () => {
    if (!reporte.comprobante_storage_path) return
    setLeyendo(true); setOcrError(null)
    try {
      const { data: u } = await supabase.storage.from('reportes').createSignedUrl(reporte.comprobante_storage_path, 120)
      if (!u?.signedUrl) throw new Error('No se pudo abrir el comprobante')
      const blob = await (await fetch(u.signedUrl)).blob()
      if (blob.type === 'application/pdf') throw new Error('El comprobante es PDF: revisalo a ojo')
      const image_base64 = await blobToBase64(blob)
      const { data, error } = await supabase.functions.invoke('comprobante-ocr-verify', {
        body: { image_base64, media_type: blob.type || 'image/jpeg', monto_declarado: reporte.monto, cuenta_destino_declarada: reporte.cuenta_numero },
      })
      if (error) throw new Error(error.message)
      const res = data as OcrBoleta
      setOcr(res)
      await supabase.rpc('guardar_ocr_reporte_pago_admin', { p_reporte_id: reporte.id, p_ocr: res })
      const m = res?.extraido?.es_comprobante_valido !== false ? res?.extraido?.monto : null
      if (m != null) setMontoAplicar(Number(m).toFixed(2))
    } catch (e) {
      setOcrError(e instanceof Error ? e.message : String(e))
    } finally {
      setLeyendo(false)
    }
  }
  // Leer la boleta sola al abrir un reporte de crédito pendiente que todavía no se leyó.
  useEffect(() => {
    if (reporte.estado === 'pendiente' && reporte.concepto === 'CREDITO_CUOTA' && reporte.comprobante_storage_path && !reporte.ocr_extraido) {
      void leerBoleta()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reporte.id])

  // Cargar signed URL del comprobante + caja activa
  useEffect(() => {
    void (async () => {
      if (reporte.comprobante_storage_path) {
        setComprobanteLoading(true)
        const { data, error } = await supabase.storage
          .from('reportes')
          .createSignedUrl(reporte.comprobante_storage_path, 60 * 60)
        setComprobanteLoading(false)
        if (!error && data?.signedUrl) setComprobanteUrl(data.signedUrl)
      }

      if (reporte.estado === 'pendiente') {
        // La caja física activa de la cooperativa. El campo activa=true marca
        // la caja como vigente (no histórica); la sesión abierta vive en
        // caja_sesion, pero registrar_pago_credito_admin solo necesita el id
        // de la caja, no el de la sesión.
        const { data, error } = await supabase
          .from('caja')
          .select('id, codigo, nombre, tipo, activa')
          .eq('activa', true)
          .eq('tipo', 'FISICA')
          .limit(1)
        // Log para diagnóstico — nos dice si RLS está filtrando o si simplemente
        // no hay caja. Si el error aparece, lo mostramos al operador en lugar
        // del mensaje genérico.
        // eslint-disable-next-line no-console
        console.log('[reportes-pago] caja query →', { data, error })
        if (error) {
          setCajaError('Error consultando caja: ' + error.message)
          setCajaActiva(null)
        } else if (!data || data.length === 0) {
          setCajaError('No encontramos una caja física activa. Verificá /neocore/caja.')
          setCajaActiva(null)
        } else {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const c = data[0] as any
          setCajaActiva({ id: c.id, nombre: c.nombre ?? c.codigo ?? 'Caja' })
        }
      }
      // Cuota de crédito: traer el pendiente real para detectar abonos parciales.
      if (reporte.concepto === 'CREDITO_CUOTA' && reporte.credito_cuota_id) {
        const { data: cc } = await supabase
          .from('credito_cuota')
          .select('capital, interes, mora_acumulada, capital_pagado, interes_pagado, mora_pagada')
          .eq('id', reporte.credito_cuota_id)
          .maybeSingle()
        if (cc) {
          const pend =
            Math.max(Number(cc.capital) - Number(cc.capital_pagado), 0) +
            Math.max(Number(cc.interes) - Number(cc.interes_pagado), 0) +
            Math.max(Number(cc.mora_acumulada) - Number(cc.mora_pagada), 0)
          setCuotaPend(pend)
        }
      }
      // Depósito a ahorro: cargar cuentas de entrada (banco/caja donde llegó el dinero).
      if (reporte.estado === 'pendiente' && reporte.concepto === 'AHORRO_DEPOSITO') {
        const { data } = await supabase.rpc('listar_cuentas_entrada_deposito')
        const list = (data ?? []) as Array<{ codigo: string; nombre: string; tipo: string }>
        setCuentasEntrada(list)
        // Preseleccionar la cuenta destino del reporte; si no matchea, primer banco.
        const match = list.find((c) => c.codigo === reporte.cuenta_contable_codigo)
        const pre = match ?? list.find((c) => c.tipo === 'BANCO') ?? list[0]
        if (pre) setEntradaCodigo(pre.codigo)
      }
    })()
  }, [reporte.id, reporte.comprobante_storage_path, reporte.estado, reporte.concepto, reporte.credito_cuota_id])

  // Guarda ambas referencias (boleta + autorización) si cambiaron, validando
  // duplicados. Devuelve false si falló (para no aplicar el pago).
  const guardarReferencias = async (): Promise<boolean> => {
    const b = refBoleta.trim(), a = refBanco.trim()
    const sinCambios = (b === (reporte.numero_referencia ?? '')) && (a === (reporte.referencia_banco ?? ''))
    if (sinCambios) return true
    const { error } = await supabase.rpc('set_referencias_reporte_admin', {
      p_reporte_id: reporte.id, p_numero_referencia: b, p_referencia_banco: a,
    })
    if (error) { setResultMsg({ kind: 'err', text: error.message }); return false }
    return true
  }

  const aplicarAhorro = async () => {
    if (!entradaCodigo) { setResultMsg({ kind: 'err', text: 'Elegí la cuenta donde entró el depósito' }); return }
    if (!refBoleta.trim() || !refBanco.trim()) { setResultMsg({ kind: 'err', text: 'Cargá la autorización y la referencia antes de aplicar' }); return }
    setWorking('aplicar')
    setResultMsg(null)
    if (!(await guardarReferencias())) { setWorking(null); return }
    const { error } = await supabase.rpc('conciliar_reporte_pago_ahorro_admin', {
      p_reporte_id: reporte.id,
      p_cuenta_entrada_codigo: entradaCodigo,
      p_nota: null,
    })
    setWorking(null)
    if (error) { setResultMsg({ kind: 'err', text: error.message }); return }
    setResultMsg({ kind: 'ok', text: 'Depósito acreditado al ahorro.' })
    setTimeout(onResolved, 900)
  }

  const aplicar = async () => {
    if (!cajaActiva) { setCajaError('No hay caja activa abierta'); return }
    if (!refBoleta.trim() || !refBanco.trim()) { setResultMsg({ kind: 'err', text: 'Cargá la autorización y la referencia antes de aplicar' }); return }
    setWorking('aplicar')
    setResultMsg(null)
    if (!(await guardarReferencias())) { setWorking(null); return }
    if (!(montoNum > 0)) { setWorking(null); setResultMsg({ kind: 'err', text: 'Monto a aplicar inválido' }); return }
    if (difOcr && !confirmoDif) { setWorking(null); setResultMsg({ kind: 'err', text: `La boleta dice ${fmtQ(ocrMonto ?? 0)}. Revisá la imagen o confirmá la diferencia.` }); return }
    if (difReportado && !difOcr && ocrMonto == null && !notaMonto.trim()) { setWorking(null); setResultMsg({ kind: 'err', text: 'Cambiaste el monto: escribí una nota del porqué.' }); return }
    const { error } = await supabase.rpc('conciliar_reporte_pago_credito_admin', {
      p_reporte_id: reporte.id,
      p_caja_id: cajaActiva.id,
      p_notas: notaMonto.trim() || null,
      p_monto: montoNum,
      p_confirmar_diferencia: confirmoDif,
    })
    setWorking(null)
    if (error) {
      setResultMsg({ kind: 'err', text: error.message })
      return
    }
    setResultMsg({ kind: 'ok', text: 'Pago aplicado al crédito.' })
    setTimeout(onResolved, 900)
  }

  const rechazar = async () => {
    if (!motivoRechazo.trim()) {
      setResultMsg({ kind: 'err', text: 'Tenés que escribir un motivo de rechazo' })
      return
    }
    setWorking('rechazar')
    setResultMsg(null)
    const { error } = await supabase.rpc('rechazar_reporte_pago_admin', {
      p_reporte_id: reporte.id,
      p_motivo: motivoRechazo.trim(),
    })
    setWorking(null)
    if (error) {
      setResultMsg({ kind: 'err', text: error.message })
      return
    }
    setResultMsg({ kind: 'ok', text: 'Reporte rechazado.' })
    setTimeout(onResolved, 900)
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', justifyContent: 'flex-end', zIndex: 50,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(560px, 100%)', height: '100%',
          background: '#FBF7F3', overflowY: 'auto',
          boxShadow: '-12px 0 36px rgba(0,0,0,0.18)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid var(--hairline)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <p style={{
              margin: 0, fontSize: 10, fontWeight: 800, letterSpacing: 2,
              color: 'var(--brand-700)', textTransform: 'uppercase',
            }}>Reporte de pago</p>
            <p style={{ margin: '4px 0 0', fontSize: 16, fontWeight: 700, color: 'var(--ink)' }}>
              {reporte.asociado_nombre}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              all: 'unset', cursor: 'pointer',
              padding: '6px 10px', borderRadius: 8,
              background: 'var(--n-100)', fontSize: 12, fontWeight: 700,
              color: 'var(--ink-soft)',
            }}
          >Cerrar</button>
        </div>

        {/* Body */}
        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>

          {/* Resumen del depósito */}
          <Section title="Depósito reportado">
            <KV k="Concepto" v={(CONCEPTO_LABEL[reporte.concepto] ?? CONCEPTO_LABEL.OTRO).label} highlight />
            <KV k="Monto" v={fmtQ(reporte.monto)} highlight />
            <KV k="Fecha" v={fmtFecha(reporte.fecha_movimiento)} />
            <KV k="Forma" v={reporte.forma_pago === 'TRANSFERENCIA' ? 'Transferencia' : 'Depósito bancario'} />
            {reporte.numero_referencia && <KV k="Autorización" v={reporte.numero_referencia} />}
            {reporte.referencia_banco && <KV k="Referencia" v={reporte.referencia_banco} />}
            <KV k="Cuenta destino" v={(reporte.cuenta_alias || reporte.cuenta_banco || '—') + (reporte.cuenta_numero ? ' · ' + reporte.cuenta_numero : '')} />
            <KV k="Asociado" v={`NEO-${reporte.asociado_no}`} />
            {reporte.credito_tipo_nombre && (
              <KV k="Crédito" v={reporte.credito_tipo_nombre + (reporte.cuota_numero != null ? ' · cuota ' + reporte.cuota_numero : '')} />
            )}
            {reporte.concepto === 'CREDITO_CUOTA' && cuotaPend != null && (
              <KV k="Pendiente cuota" v={fmtQ(cuotaPend)} />
            )}
            {reporte.notas_asociado && (
              <KV k="Notas" v={reporte.notas_asociado} />
            )}
          </Section>

          {/* Abono parcial / exceso · ADR-017 */}
          {/* Lectura de la boleta con IA · paso extra de verificación (0413) */}
          {reporte.concepto === 'CREDITO_CUOTA' && reporte.estado === 'pendiente' && (
            <Section title="Lectura de la boleta (IA)">
              {leyendo ? (
                <p style={{ margin: 0, fontSize: 12.5, color: 'var(--muted-foreground)' }}>Leyendo la boleta…</p>
              ) : ocr?.extraido ? (
                <>
                  <KV k="Monto en la boleta" v={ocrMonto != null ? fmtQ(ocrMonto) : 'No se pudo leer'} highlight />
                  {ocr.extraido.referencia && <KV k="Referencia leída" v={ocr.extraido.referencia} />}
                  {ocr.extraido.fecha && <KV k="Fecha leída" v={ocr.extraido.fecha} />}
                  {ocr.extraido.cuenta_destino && <KV k="Cuenta destino leída" v={`${ocr.extraido.banco_destino ?? ''} ${ocr.extraido.cuenta_destino}`} />}
                  <KV k="Confianza" v={ocr.extraido.confianza} />
                  {ocrMonto != null && Math.abs(ocrMonto - reporte.monto) > 0.009 && (
                    <div style={{ marginTop: 8, padding: 10, borderRadius: 10, background: 'rgba(163,45,45,0.08)', color: 'var(--danger, #a32d2d)', fontSize: 12.5 }}>
                      ⚠️ El asociado reportó <strong>{fmtQ(reporte.monto)}</strong> pero la boleta dice <strong>{fmtQ(ocrMonto)}</strong>. Se aplica lo de la boleta.
                    </div>
                  )}
                  {ocr.verificacion?.coincide_cuenta_institucional === false && (
                    <div style={{ marginTop: 8, padding: 10, borderRadius: 10, background: 'rgba(217,119,6,0.10)', color: 'var(--warning, #d97706)', fontSize: 12.5 }}>
                      ⚠️ La cuenta destino de la boleta no parece ser de Neocoop. Revisala antes de aplicar.
                    </div>
                  )}
                  {ocr.extraido.es_comprobante_valido === false && (
                    <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--danger, #a32d2d)' }}>
                      La imagen no parece un comprobante válido{ocr.extraido.motivo_invalido ? `: ${ocr.extraido.motivo_invalido}` : ''}.
                    </div>
                  )}
                </>
              ) : (
                <p style={{ margin: 0, fontSize: 12.5, color: 'var(--muted-foreground)' }}>
                  {ocrError ? `No se pudo leer: ${ocrError}` : reporte.comprobante_storage_path ? 'Sin leer todavía.' : 'El reporte no tiene comprobante.'}
                </p>
              )}
              {reporte.comprobante_storage_path && !leyendo && (
                <button type="button" className="btn btn-ghost" onClick={() => void leerBoleta()} style={{ marginTop: 8, fontSize: 12 }}>
                  {ocr ? 'Volver a leer' : 'Leer boleta'}
                </button>
              )}
              <div style={{ marginTop: 12, display: 'grid', gap: 6 }}>
                <label style={{ fontSize: 10, fontWeight: 800, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1 }}>
                  Monto a aplicar (el de la boleta)
                </label>
                <input value={montoAplicar} onChange={(e) => setMontoAplicar(e.target.value.replace(/[^0-9.]/g, ''))}
                  style={{ padding: '9px 11px', border: '1px solid var(--hairline)', borderRadius: 8, fontSize: 15, fontWeight: 700, fontFamily: 'inherit' }} />
                {difOcr && (
                  <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, color: 'var(--danger, #a32d2d)' }}>
                    <input type="checkbox" checked={confirmoDif} onChange={(e) => setConfirmoDif(e.target.checked)} />
                    Confirmo que la boleta es por {fmtQ(montoNum)} aunque la lectura dice {fmtQ(ocrMonto ?? 0)}
                  </label>
                )}
                {difReportado && (
                  <input value={notaMonto} onChange={(e) => setNotaMonto(e.target.value)}
                    placeholder="Nota (ej. la boleta es por Q550, el asociado reportó la cuota completa)"
                    style={{ padding: '8px 10px', border: '1px solid var(--hairline)', borderRadius: 8, fontSize: 12.5, fontFamily: 'inherit' }} />
                )}
              </div>
            </Section>
          )}

          {reporte.estado === 'pendiente' && reporte.concepto === 'CREDITO_CUOTA' && cuotaPend != null && (
            montoNum > cuotaPend + 0.005 ? (
              <div style={{
                padding: 12, borderRadius: 10,
                background: 'rgba(217,119,6,0.10)', color: 'var(--warning, #d97706)',
                fontSize: 12.5, lineHeight: 1.5,
              }}>
                El monto a aplicar ({fmtQ(montoNum)}) <strong>supera el pendiente de la cuota</strong> ({fmtQ(cuotaPend)}).
                Al aplicar va a fallar: verificá que apunte a la cuota correcta o rechazalo.
              </div>
            ) : montoNum < cuotaPend - 0.005 ? (
              <div style={{
                padding: 12, borderRadius: 10,
                background: 'rgba(37,99,235,0.08)', color: '#1d4ed8',
                fontSize: 12.5, lineHeight: 1.5,
              }}>
                <strong>Abono parcial.</strong> Cubre {fmtQ(montoNum)} de {fmtQ(cuotaPend)};
                la cuota queda en <em>pagada parcial</em> y restarán {fmtQ(cuotaPend - montoNum)}.
                El asociado completa con otra boleta.
              </div>
            ) : null
          )}

          {/* Acciones */}
          {reporte.estado === 'pendiente' ? (
            <Section title="Acciones">
              {/* Solo créditos tienen RPC de conciliación por ahora. Ahorros y
                  PF requieren SQL manual · RPC pendiente de crear en 1.3.x. */}
              {reporte.concepto !== 'CREDITO_CUOTA' && reporte.concepto !== 'AHORRO_DEPOSITO' && (
                <div style={{
                  padding: 12, borderRadius: 10,
                  background: 'rgba(217,119,6,0.10)', color: 'var(--warning, #d97706)',
                  display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, marginBottom: 8,
                }}>
                  <Warning size={16} weight="duotone" style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>
                    <strong>Aplicación manual por ahora.</strong> Las aperturas de PF todavía no tienen conciliación automática. Verificá el comprobante y avisale al asociado por WhatsApp que ya lo acreditaste. Se puede rechazar desde acá si no cuadra.
                  </span>
                </div>
              )}
              {reporte.concepto === 'AHORRO_DEPOSITO' && (
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-soft)', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                    ¿A qué cuenta entró el depósito?
                  </label>
                  <select
                    value={entradaCodigo}
                    onChange={(e) => setEntradaCodigo(e.target.value)}
                    style={{
                      width: '100%', marginTop: 6, padding: '10px 12px', borderRadius: 10,
                      border: '1px solid var(--hairline)', background: 'var(--surface)', fontSize: 13,
                    }}
                  >
                    {cuentasEntrada.length === 0 && <option value="">Cargando cuentas…</option>}
                    {cuentasEntrada.map((c) => (
                      <option key={c.codigo} value={c.codigo}>{c.nombre}</option>
                    ))}
                  </select>
                </div>
              )}
              {(reporte.concepto === 'AHORRO_DEPOSITO' || reporte.concepto === 'CREDITO_CUOTA') && (
                <div style={{ marginBottom: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div>
                    <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                      Autorización <span style={{ color: 'var(--danger, #a32d2d)' }}>*</span>
                    </label>
                    <input
                      value={refBoleta}
                      onChange={(e) => setRefBoleta(e.target.value)}
                      placeholder="del asociado"
                      style={{
                        width: '100%', marginTop: 4, padding: '9px 11px', borderRadius: 10,
                        border: '1px solid var(--hairline)', background: 'var(--surface)', fontSize: 13,
                        fontFamily: 'var(--font-mono, monospace)',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                      Referencia <span style={{ color: 'var(--danger, #a32d2d)' }}>*</span>
                    </label>
                    <input
                      value={refBanco}
                      onChange={(e) => setRefBanco(e.target.value)}
                      placeholder="tu estado de cuenta"
                      style={{
                        width: '100%', marginTop: 4, padding: '9px 11px', borderRadius: 10,
                        border: '1px solid var(--hairline)', background: 'var(--surface)', fontSize: 13,
                        fontFamily: 'var(--font-mono, monospace)',
                      }}
                    />
                  </div>
                </div>
              )}
              {reporte.concepto === 'CREDITO_CUOTA' && cajaError && (
                <div style={{
                  padding: 12, borderRadius: 10,
                  background: 'rgba(217,119,6,0.10)', color: 'var(--warning, #d97706)',
                  display: 'flex', gap: 8, alignItems: 'center', fontSize: 12,
                }}>
                  <Warning size={16} weight="duotone" />
                  <span>{cajaError}</span>
                </div>
              )}
              {reporte.concepto === 'CREDITO_CUOTA' && cajaActiva && (
                <div style={{
                  padding: 10, borderRadius: 10,
                  background: 'var(--brand-50)', color: 'var(--brand-700)',
                  fontSize: 12, fontWeight: 600, marginBottom: 8,
                }}>
                  Caja activa: <strong>{cajaActiva.nombre}</strong>
                </div>
              )}

              {!mostrarRechazo ? (
                <div style={{ display: 'flex', gap: 8 }}>
                  {reporte.concepto === 'CREDITO_CUOTA' && (
                    <button
                      type="button"
                      onClick={aplicar}
                      disabled={!cajaActiva || working !== null}
                      style={{
                        all: 'unset', cursor: cajaActiva && !working ? 'pointer' : 'not-allowed',
                        padding: '12px 16px', borderRadius: 12,
                        background: cajaActiva && !working ? 'var(--brand-600)' : 'var(--n-300)',
                        color: '#fff', fontSize: 13, fontWeight: 700,
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        flex: 1, justifyContent: 'center',
                      }}
                    >
                      <Coins size={16} weight="bold" />
                      {working === 'aplicar' ? 'Aplicando…' : 'Aplicar al crédito'}
                    </button>
                  )}
                  {reporte.concepto === 'AHORRO_DEPOSITO' && (
                    <button
                      type="button"
                      onClick={aplicarAhorro}
                      disabled={!entradaCodigo || working !== null}
                      style={{
                        all: 'unset', cursor: entradaCodigo && !working ? 'pointer' : 'not-allowed',
                        padding: '12px 16px', borderRadius: 12,
                        background: entradaCodigo && !working ? 'var(--brand-600)' : 'var(--n-300)',
                        color: '#fff', fontSize: 13, fontWeight: 700,
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        flex: 1, justifyContent: 'center',
                      }}
                    >
                      <Coins size={16} weight="bold" />
                      {working === 'aplicar' ? 'Aplicando…' : 'Aplicar al ahorro'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setMostrarRechazo(true)}
                    disabled={working !== null}
                    style={{
                      all: 'unset', cursor: 'pointer',
                      padding: '12px 14px', borderRadius: 12,
                      background: 'var(--surface)', color: 'var(--ink)',
                      border: '1px solid var(--hairline)',
                      fontSize: 13, fontWeight: 600,
                    }}
                  >
                    Rechazar
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <textarea
                    value={motivoRechazo}
                    onChange={(e) => setMotivoRechazo(e.target.value)}
                    placeholder="Motivo del rechazo (lo verá el asociado)"
                    rows={3}
                    style={{
                      padding: 10, borderRadius: 10,
                      border: '1px solid var(--hairline)', background: '#fff',
                      fontSize: 13, color: 'var(--ink)', resize: 'vertical',
                      fontFamily: 'inherit',
                    }}
                  />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      onClick={rechazar}
                      disabled={working !== null || !motivoRechazo.trim()}
                      style={{
                        all: 'unset', cursor: 'pointer',
                        padding: '10px 14px', borderRadius: 12,
                        background: 'var(--danger, #a32d2d)', color: '#fff',
                        fontSize: 13, fontWeight: 700, flex: 1, textAlign: 'center',
                      }}
                    >
                      {working === 'rechazar' ? 'Rechazando…' : 'Confirmar rechazo'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setMostrarRechazo(false); setMotivoRechazo('') }}
                      style={{
                        all: 'unset', cursor: 'pointer',
                        padding: '10px 14px', borderRadius: 12,
                        background: 'var(--surface)', color: 'var(--ink-soft)',
                        border: '1px solid var(--hairline)',
                        fontSize: 13, fontWeight: 600,
                      }}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}

              {resultMsg && (
                <div style={{
                  marginTop: 10, padding: 10, borderRadius: 10, fontSize: 12,
                  background: resultMsg.kind === 'ok' ? 'rgba(15,138,87,0.10)' : 'rgba(163,45,45,0.10)',
                  color: resultMsg.kind === 'ok' ? 'var(--ok-ink, #0f8a57)' : 'var(--danger, #a32d2d)',
                }}>
                  {resultMsg.text}
                </div>
              )}
            </Section>
          ) : (
            <Section title="Resultado">
              <KV k="Estado" v={ESTADO_BADGE[reporte.estado].label} />
              {reporte.estado === 'conciliado' && reporte.conciliado_at && (
                <KV k="Aplicado" v={fmtFechaHora(reporte.conciliado_at)} />
              )}
              {reporte.estado === 'rechazado' && reporte.rechazo_motivo && (
                <KV k="Motivo" v={reporte.rechazo_motivo} />
              )}
            </Section>
          )}

          {/* Comprobante · anexo de verificación (al fondo) */}
          <Section title="Comprobante · verificación">
            {comprobanteLoading ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-soft)' }}>Cargando…</div>
            ) : !reporte.comprobante_storage_path ? (
              <div style={{
                padding: 20, borderRadius: 12,
                background: 'var(--n-100)', color: 'var(--ink-soft)',
                fontSize: 12, textAlign: 'center',
              }}>
                <ImageSquare size={26} weight="duotone" />
                <p style={{ margin: '6px 0 0' }}>Sin comprobante adjunto.</p>
              </div>
            ) : comprobanteUrl ? (
              <a href={comprobanteUrl} target="_blank" rel="noopener noreferrer">
                <img
                  src={comprobanteUrl}
                  alt="Comprobante"
                  style={{
                    width: '100%', borderRadius: 12, border: '1px solid var(--hairline)',
                    objectFit: 'contain', maxHeight: 360,
                  }}
                />
                <p style={{
                  margin: '6px 0 0', fontSize: 11, color: 'var(--brand-700)',
                  fontWeight: 700, textAlign: 'right',
                }}>Abrir en tamaño completo →</p>
              </a>
            ) : (
              <p style={{ fontSize: 12, color: 'var(--ink-soft)' }}>No pudimos cargar el comprobante.</p>
            )}
          </Section>

        </div>
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p style={{
        margin: '0 0 10px', fontSize: 10, fontWeight: 800, letterSpacing: 1.8,
        color: 'var(--ink-soft)', textTransform: 'uppercase',
      }}>{title}</p>
      <div style={{
        padding: 14, background: '#fff', borderRadius: 12,
        border: '1px solid var(--hairline)',
      }}>
        {children}
      </div>
    </div>
  )
}

function KV({ k, v, highlight }: { k: string; v: string; highlight?: boolean }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      padding: '6px 0',
      borderBottom: '1px solid var(--hairline)',
    }}>
      <span style={{ fontSize: 12, color: 'var(--ink-soft)' }}>{k}</span>
      <span style={{
        fontSize: highlight ? 16 : 13,
        fontWeight: highlight ? 800 : 600,
        color: highlight ? 'var(--brand-700)' : 'var(--ink)',
        fontFeatureSettings: '"tnum"',
        textAlign: 'right',
      }}>{v}</span>
    </div>
  )
}
